import React, { useState, useEffect, useRef } from 'react';
import { 
  DollarSign, 
  MessageCircle, 
  Send, 
  CheckCircle, 
  XCircle, 
  Clock,
  RefreshCw,
  Search,
  AlertCircle
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { useToast } from '../../hooks/use-toast';

const WithdrawalManagement = () => {
  const { toast } = useToast();
  const [withdrawals, setWithdrawals] = useState([]);
  const [selectedWithdrawal, setSelectedWithdrawal] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [allWithdrawals, setAllWithdrawals] = useState([]); // Store all withdrawals for counting
  const messagesEndRef = useRef(null);
  const pollIntervalRef = useRef(null);

  // Load all withdrawals on mount (for counts)
  useEffect(() => {
    loadAllWithdrawals();
  }, []);

  // Load filtered withdrawals when statusFilter changes
  useEffect(() => {
    loadWithdrawals();
  }, [statusFilter]);

  // Poll for updates every 10 seconds
  useEffect(() => {
    pollIntervalRef.current = setInterval(() => {
      loadWithdrawals();
      loadAllWithdrawals();
      if (selectedWithdrawal) {
        loadMessages(selectedWithdrawal.withdrawal_id);
      }
    }, 10000);
    
    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, [selectedWithdrawal]);

  useEffect(() => {
    if (messages.length > 0) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  const loadAllWithdrawals = async () => {
    try {
      const token = localStorage.getItem('secure_admin_token') || localStorage.getItem('admin_token') || localStorage.getItem('token');
      // Use relative URL - works on any domain
      const response = await fetch('/api/withdrawal-requests/all', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setAllWithdrawals(data);
      }
    } catch (error) {
      console.error('Error loading all withdrawals:', error);
    }
  };

  const loadWithdrawals = async () => {
    try {
      const token = localStorage.getItem('secure_admin_token') || localStorage.getItem('admin_token') || localStorage.getItem('token');
      // Use relative URL - works on any domain
      const url = statusFilter === 'all' 
        ? '/api/withdrawal-requests/all'
        : `/api/withdrawal-requests/all?status=${statusFilter}`;
      
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setWithdrawals(data);
      } else {
        console.error('Failed to load withdrawals:', response.status);
      }
    } catch (error) {
      console.error('Error loading withdrawals:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadMessages = async (withdrawalId) => {
    try {
      const token = localStorage.getItem('secure_admin_token') || localStorage.getItem('admin_token') || localStorage.getItem('token');
      // Use relative URL - works on any domain
      const response = await fetch(`/api/withdrawal-requests/${withdrawalId}/messages`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setMessages(data.messages);
        setSelectedWithdrawal(prev => ({
          ...prev,
          ...data.withdrawal_info
        }));
      }
    } catch (error) {
      console.error('Error loading messages:', error);
    }
  };

  const handleSelectWithdrawal = (withdrawal) => {
    setSelectedWithdrawal(withdrawal);
    loadMessages(withdrawal.withdrawal_id);
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim()) return;
    if (!selectedWithdrawal) return;

    setSending(true);
    try {
      const token = localStorage.getItem('secure_admin_token') || localStorage.getItem('admin_token') || localStorage.getItem('token');
      // Use relative URL - works on any domain
      const formData = new FormData();
      formData.append('message_text', newMessage.trim());
      
      const response = await fetch(`/api/withdrawal-requests/${selectedWithdrawal.withdrawal_id}/messages`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });
      
      if (response.ok) {
        const sentMessage = await response.json();
        setMessages(prev => [...prev, sentMessage]);
        setNewMessage('');
        
        toast({
          title: "Message sent",
          description: "User will be notified"
        });
      }
    } catch (error) {
      console.error('Error sending message:', error);
      toast({
        title: "Error",
        description: "Failed to send message",
        variant: "destructive"
      });
    } finally {
      setSending(false);
    }
  };

  const handleUpdateStatus = async (status, note = '') => {
    if (!selectedWithdrawal) return;

    try {
      const token = localStorage.getItem('secure_admin_token') || localStorage.getItem('admin_token') || localStorage.getItem('token');
      // Use relative URL - works on any domain
      const formData = new FormData();
      formData.append('status', status);
      if (note) formData.append('admin_note', note);
      
      const response = await fetch(`/api/withdrawal-requests/${selectedWithdrawal.withdrawal_id}/status`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });
      
      if (response.ok) {
        toast({
          title: "Status Updated",
          description: `Withdrawal marked as ${status}`
        });
        loadWithdrawals();
        loadMessages(selectedWithdrawal.withdrawal_id);
      }
    } catch (error) {
      console.error('Error updating status:', error);
      toast({
        title: "Error",
        description: "Failed to update status",
        variant: "destructive"
      });
    }
  };

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleString();
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'bg-yellow-500/20 text-yellow-400 border-yellow-500/50';
      case 'processing': return 'bg-blue-500/20 text-blue-400 border-blue-500/50';
      case 'completed': return 'bg-green-500/20 text-green-400 border-green-500/50';
      case 'rejected': return 'bg-red-500/20 text-red-400 border-red-500/50';
      case 'cancelled': return 'bg-gray-500/20 text-gray-400 border-gray-500/50';
      default: return 'bg-slate-500/20 text-slate-400 border-slate-500/50';
    }
  };

  // Count withdrawals by status (from allWithdrawals for accurate counts)
  const statusCounts = {
    all: allWithdrawals.length,
    pending: allWithdrawals.filter(w => w.status === 'pending').length,
    processing: allWithdrawals.filter(w => w.status === 'processing').length,
    completed: allWithdrawals.filter(w => w.status === 'completed').length,
    rejected: allWithdrawals.filter(w => w.status === 'rejected').length,
    cancelled: allWithdrawals.filter(w => w.status === 'cancelled').length,
  };

  const filteredWithdrawals = withdrawals.filter(w => 
    w.username.toLowerCase().includes(searchTerm.toLowerCase()) ||
    w.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return <div className="flex items-center justify-center h-screen text-cyan-400">Loading...</div>;
  }

  return (
    <div className="flex h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-cyan-900">
      {/* Sidebar - Withdrawal Requests */}
      <div className="w-96 bg-slate-800/50 backdrop-blur-sm border-r border-cyan-500/20 flex flex-col">
        <div className="p-4 border-b border-cyan-500/20">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold text-cyan-400 flex items-center gap-2">
              <DollarSign className="w-6 h-6" />
              Withdrawals ({statusCounts.all})
            </h2>
            <button onClick={loadWithdrawals} className="p-2 hover:bg-cyan-500/10 rounded-lg text-cyan-400">
              <RefreshCw className="w-5 h-5" />
            </button>
          </div>
          
          {/* Status Filter */}
          <div className="grid grid-cols-3 gap-2 mb-4">
            <button
              onClick={() => setStatusFilter('all')}
              className={`px-3 py-2 rounded-lg text-sm font-semibold transition ${
                statusFilter === 'all'
                  ? 'bg-cyan-500 text-white'
                  : 'bg-slate-700/50 text-cyan-600 hover:bg-slate-700'
              }`}
            >
              All ({statusCounts.all})
            </button>
            <button
              onClick={() => setStatusFilter('pending')}
              className={`px-3 py-2 rounded-lg text-sm font-semibold transition ${
                statusFilter === 'pending'
                  ? 'bg-yellow-500 text-white'
                  : 'bg-slate-700/50 text-cyan-600 hover:bg-slate-700'
              }`}
            >
              Pending ({statusCounts.pending})
            </button>
            <button
              onClick={() => setStatusFilter('processing')}
              className={`px-3 py-2 rounded-lg text-sm font-semibold transition ${
                statusFilter === 'processing'
                  ? 'bg-blue-500 text-white'
                  : 'bg-slate-700/50 text-cyan-600 hover:bg-slate-700'
              }`}
            >
              Processing ({statusCounts.processing})
            </button>
            <button
              onClick={() => setStatusFilter('completed')}
              className={`px-3 py-2 rounded-lg text-sm font-semibold transition ${
                statusFilter === 'completed'
                  ? 'bg-green-500 text-white'
                  : 'bg-slate-700/50 text-cyan-600 hover:bg-slate-700'
              }`}
            >
              Completed ({statusCounts.completed})
            </button>
            <button
              onClick={() => setStatusFilter('rejected')}
              className={`px-3 py-2 rounded-lg text-sm font-semibold transition ${
                statusFilter === 'rejected'
                  ? 'bg-red-500 text-white'
                  : 'bg-slate-700/50 text-cyan-600 hover:bg-slate-700'
              }`}
            >
              Rejected ({statusCounts.rejected})
            </button>
            <button
              onClick={() => setStatusFilter('cancelled')}
              className={`px-3 py-2 rounded-lg text-sm font-semibold transition ${
                statusFilter === 'cancelled'
                  ? 'bg-gray-500 text-white'
                  : 'bg-slate-700/50 text-cyan-600 hover:bg-slate-700'
              }`}
            >
              Cancelled ({statusCounts.cancelled})
            </button>
          </div>
          
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-cyan-600" />
            <Input
              type="text"
              placeholder="Search users..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 bg-slate-700/50 border-cyan-500/30 text-cyan-100"
            />
          </div>
        </div>

        <div className="flex-1 overflow-y-auto">
          {filteredWithdrawals.length === 0 ? (
            <div className="p-8 text-center">
              <AlertCircle className="w-12 h-12 text-cyan-600 mx-auto mb-3" />
              <p className="text-cyan-400 font-semibold mb-1">No Withdrawals Found</p>
              <p className="text-cyan-600 text-sm">
                {statusFilter === 'all' ? 'No withdrawal requests yet' : `No ${statusFilter} withdrawals`}
              </p>
            </div>
          ) : (
            filteredWithdrawals.map((withdrawal) => (
              <div
                key={withdrawal.withdrawal_id}
                onClick={() => handleSelectWithdrawal(withdrawal)}
                className={`p-4 border-b border-cyan-500/10 cursor-pointer transition ${
                  selectedWithdrawal?.withdrawal_id === withdrawal.withdrawal_id
                    ? 'bg-cyan-500/20 border-l-4 border-l-cyan-500'
                    : 'hover:bg-slate-700/30'
                }`}
              >
                <div className="flex items-start justify-between mb-2">
                  <div className="flex-1">
                    <h3 className="font-semibold text-cyan-300">{withdrawal.username}</h3>
                    <p className="text-xs text-cyan-600 truncate">{withdrawal.email}</p>
                  </div>
                  {withdrawal.unread_messages > 0 && (
                    <span className="bg-red-500 text-white text-xs px-2 py-0.5 rounded-full font-bold">
                      {withdrawal.unread_messages}
                    </span>
                  )}
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-lg font-bold text-green-400">
                    {withdrawal.amount} {(withdrawal.currency || '').toUpperCase()}
                  </span>
                  <span className={`text-xs px-2 py-1 rounded-full border font-semibold ${getStatusColor(withdrawal.status)}`}>
                    {withdrawal.status.charAt(0).toUpperCase() + withdrawal.status.slice(1)}
                  </span>
                </div>
                <p className="text-xs text-cyan-700 mt-1">{formatTime(withdrawal.created_at)}</p>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Main Area - Messages & Details */}
      <div className="flex-1 flex flex-col">
        {selectedWithdrawal ? (
          <>
            {/* Header */}
            <div className="bg-slate-800/50 backdrop-blur-sm border-b border-cyan-500/20 p-4">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-bold text-cyan-400">{selectedWithdrawal.username}</h3>
                  <p className="text-sm text-cyan-600">{selectedWithdrawal.email}</p>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-2xl font-bold text-green-400">
                    {selectedWithdrawal.amount} {(selectedWithdrawal.currency || '').toUpperCase()}
                  </span>
                </div>
              </div>
              
              <div className="mt-4 grid grid-cols-2 gap-4">
                <div className="bg-slate-700/50 p-3 rounded-lg">
                  <p className="text-xs text-cyan-600 mb-1">Wallet Address</p>
                  <p className="text-sm text-cyan-300 font-mono break-all">{selectedWithdrawal.wallet_address}</p>
                </div>
                <div className="bg-slate-700/50 p-3 rounded-lg">
                  <p className="text-xs text-cyan-600 mb-1">Status</p>
                  <span className={`text-sm px-3 py-1 rounded-full border font-semibold ${getStatusColor(selectedWithdrawal.status)}`}>
                    {selectedWithdrawal.status.charAt(0).toUpperCase() + selectedWithdrawal.status.slice(1)}
                  </span>
                </div>
              </div>
              
              {/* Action Buttons */}
              <div className="mt-4 flex gap-2">
                <Button
                  onClick={() => handleUpdateStatus('processing')}
                  className="bg-blue-500 hover:bg-blue-600 text-white"
                  size="sm"
                >
                  <Clock className="w-4 h-4 mr-1" />
                  Mark Processing
                </Button>
                <Button
                  onClick={() => handleUpdateStatus('completed', 'Transaction completed successfully')}
                  className="bg-green-500 hover:bg-green-600 text-white"
                  size="sm"
                >
                  <CheckCircle className="w-4 h-4 mr-1" />
                  Approve
                </Button>
                <Button
                  onClick={() => handleUpdateStatus('rejected', 'Withdrawal request rejected')}
                  className="bg-red-500 hover:bg-red-600 text-white"
                  size="sm"
                >
                  <XCircle className="w-4 h-4 mr-1" />
                  Reject
                </Button>
              </div>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {messages.length === 0 ? (
                <div className="text-center text-cyan-600 mt-8">
                  <MessageCircle className="w-16 h-16 mx-auto mb-4 opacity-50" />
                  <p>No messages yet. Start the conversation!</p>
                </div>
              ) : (
                messages.map((msg) => (
                  <div
                    key={msg.message_id}
                    className={`flex ${msg.sender_type === 'admin' ? 'justify-end' : 'justify-start'}`}
                  >
                    <div className={`max-w-[70%] ${
                      msg.sender_type === 'admin'
                        ? 'bg-gradient-to-r from-cyan-500 to-blue-600'
                        : 'bg-slate-700/70 border border-cyan-500/20'
                    } rounded-2xl px-4 py-3`}>
                      <p className={`text-xs font-semibold mb-1 ${
                        msg.sender_type === 'admin' ? 'text-cyan-100' : 'text-cyan-400'
                      }`}>
                        {msg.sender_name}
                      </p>
                      <p className={`text-sm ${
                        msg.sender_type === 'admin' ? 'text-white' : 'text-cyan-300'
                      }`}>
                        {msg.message_text}
                      </p>
                      <p className={`text-xs mt-1 ${
                        msg.sender_type === 'admin' ? 'text-cyan-100' : 'text-cyan-700'
                      }`}>
                        {formatTime(msg.timestamp)}
                      </p>
                    </div>
                  </div>
                ))
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Input */}
            <div className="bg-slate-800/50 backdrop-blur-sm border-t border-cyan-500/20 p-4">
              <div className="flex items-center gap-2">
                <Input
                  type="text"
                  placeholder="Type your message to user..."
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && !sending && handleSendMessage()}
                  disabled={sending}
                  className="flex-1 bg-slate-700/50 border-cyan-500/30 text-cyan-100"
                />
                <Button
                  type="button"
                  onClick={handleSendMessage}
                  disabled={sending || !newMessage.trim()}
                  className="bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500"
                >
                  {sending ? (
                    <Clock className="w-5 h-5 animate-spin" />
                  ) : (
                    <Send className="w-5 h-5" />
                  )}
                </Button>
              </div>
            </div>
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center text-cyan-600">
              <DollarSign className="w-16 h-16 mx-auto mb-4 opacity-50" />
              <p className="text-lg">Select a withdrawal request</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default WithdrawalManagement;
